/**
 * Invoice Clearing Helper
 * Handles invoice allocation for party mode receipts/payments
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { startSaveTimer } from './saveTimer';

/**
 * Get pending invoices for a party (customer or vendor)
 * @param {string} companyId
 * @param {string} partyType - 'customer' or 'vendor'
 * @param {string} partyId
 * @returns {Array} pending invoices sorted by docDate ascending
 */
export const getPendingInvoices = async (companyId, partyType, partyId) => {
  if (!companyId || !partyType || !partyId) return [];

  // Include EVERY credit document the party can owe on, not just the tax invoice:
  //  - customer: Sale Invoice, Non-Tax Sale Invoice, POS credit sale
  //  - vendor:   Purchase Invoice, Non-Tax Purchase Invoice
  // A cash POS sale posts status 'Paid' so it's naturally excluded below; only
  // unpaid (Posted/Partial) ones surface. One query PER code keeps the existing
  // (tranTypeCode + partyId + status) composite index valid — no new index.
  const codes = partyType === 'customer' ? ['SI', 'NSI', 'PS'] : ['PI', 'NPI'];

  const transRef = collection(db, 'companies', companyId, 'transactions');
  const snaps = await Promise.all(codes.map(code => getDocs(query(
    transRef,
    where('tranTypeCode', '==', code),
    where('partyId', '==', partyId),
    where('status', 'in', ['Posted', 'Partial'])
  ))));

  const invoices = snaps
    .flatMap(s => s.docs)
    .map(d => {
      const data = d.data();
      return {
        id: d.id,
        docNumber: data.docNumber,
        docDate: data.docDate,
        tranTypeCode: data.tranTypeCode,
        // Vendor's own bill no (purchase invoices) — searchable in the allocation box.
        vendorInvoiceNo: data.vendorInvoiceNo || '',
        totalAmount: data.totalAmount || 0,
        paidAmount: data.paidAmount || 0,
        balanceAmount: data.balanceAmount ?? (data.totalAmount || 0),
        status: data.status,
        // Invoice-level WHT (Sale Invoice "deduct" direction) — lets a receipt warn
        // before WHT is entered again on the same invoice. Same doc, no extra read.
        whtAmount: Number(data.whtAmount) || 0,
        whtAddToTotal: data.whtAddToTotal === true
      };
    })
    .filter(inv => inv.balanceAmount > 0)
    .sort((a, b) => (a.docDate || '').localeCompare(b.docDate || ''));

  return invoices;
};

/**
 * Get invoices for EDIT mode — includes previously allocated invoices
 * that may now be Paid, with balances virtually restored.
 * @param {string} companyId
 * @param {string} partyType - 'customer' or 'vendor'
 * @param {string} partyId
 * @param {Array} existingAllocations - [{ invoiceId, docNumber, allocatedAmount }]
 * @returns {Array} invoices with virtually restored balances
 */
export const getInvoicesForEdit = async (companyId, partyType, partyId, existingAllocations) => {
  if (!companyId || !partyType || !partyId) return [];

  // Get normally pending invoices
  const pendingInvoices = await getPendingInvoices(companyId, partyType, partyId);

  if (!existingAllocations || existingAllocations.length === 0) return pendingInvoices;

  const pendingIds = new Set(pendingInvoices.map(inv => inv.id));
  const allocMap = {};
  existingAllocations.forEach(a => { allocMap[a.invoiceId] = a.allocatedAmount || 0; });

  // Fetch invoices that were allocated but are no longer pending (now Paid)
  const missingIds = existingAllocations
    .map(a => a.invoiceId)
    .filter(id => id && !pendingIds.has(id));

  const additionalInvoices = [];
  for (const invoiceId of missingIds) {
    try {
      const invoiceRef = doc(db, 'companies', companyId, 'transactions', invoiceId);
      const invoiceDoc = await getDoc(invoiceRef);
      if (invoiceDoc.exists()) {
        const data = invoiceDoc.data();
        const allocAmt = allocMap[invoiceId] || 0;
        additionalInvoices.push({
          id: invoiceId,
          docNumber: data.docNumber,
          docDate: data.docDate,
          vendorInvoiceNo: data.vendorInvoiceNo || '',
          totalAmount: data.totalAmount || 0,
          paidAmount: Math.max(0, (data.paidAmount || 0) - allocAmt),
          balanceAmount: (data.balanceAmount || 0) + allocAmt,
          status: data.status,
          whtAmount: Number(data.whtAmount) || 0,
          whtAddToTotal: data.whtAddToTotal === true
        });
      }
    } catch (err) {
      console.error('Error fetching allocated invoice:', invoiceId, err);
    }
  }

  // For pending invoices that were partially allocated by this receipt,
  // restore their balance as if this receipt hadn't been applied yet
  const mergedInvoices = pendingInvoices.map(inv => {
    const allocAmt = allocMap[inv.id];
    if (allocAmt) {
      return {
        ...inv,
        paidAmount: Math.max(0, inv.paidAmount - allocAmt),
        balanceAmount: inv.balanceAmount + allocAmt
      };
    }
    return inv;
  });

  return [...mergedInvoices, ...additionalInvoices]
    .sort((a, b) => (a.docDate || '').localeCompare(b.docDate || ''));
};

/**
 * Apply invoice allocations atomically
 * @param {string} companyId
 * @param {Array} allocations - [{ invoiceId, docNumber, allocatedAmount }]
 * @param {string} receiptTranId - the receipt/payment transaction ID
 */
// Speed (2026-09-30): a NEW receipt/payment starts reading the invoices it will
// settle WHILE the receipt itself is being saved, instead of after. Only used
// for new documents (edits undo the old settlement first, so they read fresh).
const _prefetch = new Map(); // `${companyId}|${invoiceId}` -> { at, p }
const _PREFETCH_MS = 20000;
export const prefetchInvoices = (companyId, invoiceIds) => {
  (invoiceIds || []).forEach(id => {
    if (!companyId || !id) return;
    _prefetch.set(`${companyId}|${id}`, {
      at: Date.now(),
      p: getDoc(doc(db, 'companies', companyId, 'transactions', id)).catch(() => null),
    });
  });
};
const readInvoiceForSettle = (companyId, id) => {
  const k = `${companyId}|${id}`;
  const h = _prefetch.get(k);
  _prefetch.delete(k);
  const live = () => getDoc(doc(db, 'companies', companyId, 'transactions', id));
  if (h && Date.now() - h.at < _PREFETCH_MS) return h.p.then(snap => snap || live());
  return live();
};

export const applyInvoiceAllocations = async (companyId, allocations, receiptTranId) => {
  if (!companyId || !allocations || allocations.length === 0) return;

  const timer = startSaveTimer(`Settle ${allocations.length} invoice(s)`);
  const batch = writeBatch(db);

  // Speed: read all the invoices AT ONCE (was one after another).
  const live = allocations.filter(a => a.invoiceId && a.allocatedAmount && a.allocatedAmount > 0);
  const snaps = await Promise.all(live.map(a => readInvoiceForSettle(companyId, a.invoiceId)));
  const snapByIdx = new Map(live.map((a, i) => [a, snaps[i]]));
  timer.step('read invoices');

  // Update each invoice
  for (const alloc of allocations) {
    if (!alloc.invoiceId || !alloc.allocatedAmount || alloc.allocatedAmount <= 0) continue;

    const invoiceRef = doc(db, 'companies', companyId, 'transactions', alloc.invoiceId);
    const invoiceDoc = snapByIdx.get(alloc);

    if (!invoiceDoc.exists()) continue;

    const invoiceData = invoiceDoc.data();
    const invoiceTotal = Number(invoiceData.totalAmount) || 0;
    const currentPaid = Number(invoiceData.paidAmount) || 0;
    // ACC-11: returns are tracked separately (returnedAmount), never as "paid".
    const returned = Number(invoiceData.returnedAmount) || 0;
    const newPaidAmount = Math.round((currentPaid + alloc.allocatedAmount) * 100) / 100;

    // Guard against overpay (paid + returned cannot exceed the invoice total)
    if (newPaidAmount + returned > invoiceTotal + 0.01) {
      throw new Error(
        `Invoice ${alloc.docNumber || alloc.invoiceId} overpay: total ${invoiceTotal.toFixed(2)}, ` +
        `current paid ${currentPaid.toFixed(2)}, attempting to apply ${Number(alloc.allocatedAmount).toFixed(2)} ` +
        `(would become ${newPaidAmount.toFixed(2)}). Reduce the allocated amount to fit the outstanding balance.`
      );
    }

    const newBalanceAmount = Math.round((invoiceTotal - newPaidAmount - returned) * 100) / 100;

    let newStatus;
    if (newBalanceAmount <= 0.01) {
      newStatus = 'Paid';
    } else if (newPaidAmount > 0 || returned > 0) {
      newStatus = 'Partial';
    } else {
      newStatus = invoiceData.status;
    }

    batch.update(invoiceRef, {
      paidAmount: newPaidAmount,
      balanceAmount: Math.max(0, newBalanceAmount),
      status: newStatus,
      updatedAt: new Date().toISOString()
    });
  }

  // Store allocations on the receipt/payment transaction
  const receiptRef = doc(db, 'companies', companyId, 'transactions', receiptTranId);
  batch.update(receiptRef, {
    invoiceAllocations: allocations.filter(a => a.allocatedAmount > 0).map(a => ({
      invoiceId: a.invoiceId,
      docNumber: a.docNumber,
      allocatedAmount: a.allocatedAmount,
      // Receipt deductions (Bank/Cash Receipt): kept per invoice for view/print,
      // edit and incentive. Written ONLY when present — Firestore rejects
      // `undefined`, and payments/JV never set these.
      ...(Number(a.whtAmount) > 0 ? { whtRate: Number(a.whtRate) || 0, whtAmount: Number(a.whtAmount) } : {}),
      ...(Number(a.adjustmentAmount) > 0 ? { adjustmentAmount: Number(a.adjustmentAmount) } : {})
    })),
    updatedAt: new Date().toISOString()
  });

  await batch.commit();
  timer.step('write to server');
  timer.end();
};

/**
 * Reverse invoice allocations (for edit/delete of receipt/payment)
 * @param {string} companyId
 * @param {string} receiptTranId - the receipt/payment transaction ID
 */
export const reverseInvoiceAllocations = async (companyId, receiptTranId) => {
  if (!companyId || !receiptTranId) return;

  const receiptRef = doc(db, 'companies', companyId, 'transactions', receiptTranId);
  const receiptDoc = await getDoc(receiptRef);

  if (!receiptDoc.exists()) return;

  const receiptData = receiptDoc.data();
  const allocations = receiptData.invoiceAllocations;

  if (!allocations || allocations.length === 0) return;

  const timer = startSaveTimer(`Undo settlement of ${allocations.length} invoice(s)`);
  const batch = writeBatch(db);

  // Speed: read all the invoices AT ONCE (was one after another).
  const live = allocations.filter(a => a.invoiceId && a.allocatedAmount);
  const snaps = await Promise.all(live.map(a => getDoc(doc(db, 'companies', companyId, 'transactions', a.invoiceId))));
  const snapByIdx = new Map(live.map((a, i) => [a, snaps[i]]));
  timer.step('read invoices');

  for (const alloc of allocations) {
    if (!alloc.invoiceId || !alloc.allocatedAmount) continue;

    const invoiceRef = doc(db, 'companies', companyId, 'transactions', alloc.invoiceId);
    const invoiceDoc = snapByIdx.get(alloc);

    if (!invoiceDoc.exists()) continue;

    const invoiceData = invoiceDoc.data();
    const newPaidAmount = Math.round(Math.max(0, (invoiceData.paidAmount || 0) - alloc.allocatedAmount) * 100) / 100;
    const returned = Number(invoiceData.returnedAmount) || 0; // ACC-11
    const newBalanceAmount = Math.round(((invoiceData.totalAmount || 0) - newPaidAmount - returned) * 100) / 100;

    let newStatus;
    if (newPaidAmount <= 0 && returned <= 0) {
      newStatus = 'Posted';
    } else if (newBalanceAmount <= 0.01) {
      newStatus = 'Paid';
    } else {
      newStatus = 'Partial';
    }

    batch.update(invoiceRef, {
      paidAmount: newPaidAmount,
      balanceAmount: Math.max(0, newBalanceAmount),
      status: newStatus,
      updatedAt: new Date().toISOString()
    });
  }

  // Clear allocations from the receipt
  batch.update(receiptRef, {
    invoiceAllocations: [],
    updatedAt: new Date().toISOString()
  });

  await batch.commit();
  timer.step('write to server');
  timer.end();
};


// ─── ACC-11 (2026-09-30): returns are NOT payments ──────────────────────────
const RETURN_CODES = ['SR', 'NSR', 'PR', 'NPR'];
const LIVE_RETURN_STATUSES = ['Posted', 'Adjusted', 'Partial', 'Paid'];
const OPEN_INVOICE_STATUSES = ['Posted', 'Partial', 'Paid'];

/**
 * Recompute an invoice's returned amount from ALL its live returns and refresh
 * its balance/status. Idempotent: saving, re-posting, editing or deleting a
 * return can never double-count. Before this fix each return save ADDED its
 * total to the invoice's paidAmount (so invoices showed "Paid" with no cash,
 * collection reports overstated receipts, and re-posting a return added again).
 *
 * Legacy invoices (no `returnedAmount` yet) had their returns folded into
 * paidAmount; on first recalc that amount is taken back out of paidAmount.
 */
export const recalcInvoiceReturns = async (companyId, invoiceId) => {
  if (!companyId || !invoiceId) return null;
  const invoiceRef = doc(db, 'companies', companyId, 'transactions', invoiceId);
  const invSnap = await getDoc(invoiceRef);
  if (!invSnap.exists()) return null;
  const inv = invSnap.data();
  if (!OPEN_INVOICE_STATUSES.includes(inv.status)) return null; // Draft/Cancelled untouched

  const childSnap = await getDocs(query(
    collection(db, 'companies', companyId, 'transactions'),
    where('parentId', '==', invoiceId)
  ));
  const returned = Math.round(childSnap.docs
    .map(d => d.data())
    .filter(t => RETURN_CODES.includes(t.tranTypeCode) && LIVE_RETURN_STATUSES.includes(t.status))
    .reduce((sum, t) => sum + (Number(t.totalAmount) || 0), 0) * 100) / 100;

  const total = Number(inv.totalAmount) || 0;
  let paid = Number(inv.paidAmount) || 0;
  if (inv.returnedAmount === undefined && returned > 0) {
    // Legacy: previous code added each posted return to paidAmount.
    paid = Math.max(0, Math.round((paid - returned) * 100) / 100);
  }
  const balance = Math.max(0, Math.round((total - paid - returned) * 100) / 100);
  let status;
  if (paid <= 0 && returned <= 0) status = 'Posted';
  else if (balance <= 0.01) status = 'Paid';
  else status = 'Partial';

  const update = { returnedAmount: returned, paidAmount: paid, balanceAmount: balance, status, updatedAt: new Date().toISOString() };
  await updateDoc(invoiceRef, update);
  return update;
};

/**
 * Quantity already returned per item against an invoice (live returns only),
 * optionally ignoring the return being edited. Used to cap returnable qty.
 */
export const getReturnedQtyByItem = async (companyId, invoiceId, excludeReturnId = null) => {
  const map = {};
  if (!companyId || !invoiceId) return map;
  const snap = await getDocs(query(
    collection(db, 'companies', companyId, 'transactions'),
    where('parentId', '==', invoiceId)
  ));
  snap.docs.forEach(d => {
    if (excludeReturnId && d.id === excludeReturnId) return;
    const t = d.data();
    if (!RETURN_CODES.includes(t.tranTypeCode) || !LIVE_RETURN_STATUSES.includes(t.status)) return;
    (t.lines || []).forEach(l => {
      if (!l.itemId) return;
      map[l.itemId] = (map[l.itemId] || 0) + (Number(l.quantity) || 0);
    });
  });
  return map;
};

/** Reduce each line's returnable max (originalQty) by what was already returned. */
export const capReturnableLines = (lines, returnedByItem) => {
  const left = { ...returnedByItem };
  return lines.map(line => {
    const already = Math.min(Number(left[line.itemId]) || 0, Number(line.originalQty) || 0);
    if (already > 0) left[line.itemId] -= already;
    const cap = Math.max(0, (Number(line.originalQty) || 0) - already);
    return { ...line, originalQty: cap, alreadyReturnedQty: already, quantity: Math.min(Number(line.quantity) || 0, cap) };
  });
};
